import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthInput } from '@/components/common/AuthInput';

const registerSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Please enter a valid email'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    agreed: z.boolean().refine((val) => val === true, { message: 'You must agree to the terms' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

/** @typedef RegisterFormValues — Inferred from Zod schema. */
export type RegisterFormValues = z.infer<typeof registerSchema>;

/** @typedef RegisterFormProps — Props for the registration form. */
export interface RegisterFormProps {
  onSubmit: (data: RegisterFormValues) => Promise<void>;
  error?: string | null;
  isLoading?: boolean;
  formId: string;
  onTermsClick?: () => void;
}

/** Registration form with name, email, password fields and terms checkbox. */
export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSubmit,
  error: backendError,
  isLoading = false,
  formId,
  onTermsClick,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreed: false,
    },
  });

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className='flex w-full flex-col gap-[var(--vs-8,8px)]'
    >
      <div className='grid grid-cols-2 gap-2 fold:gap-4'>
        <AuthInput
          id='firstName'
          label='First Name'
          placeholder='Enter your first name'
          autoComplete='given-name'
          disabled={isLoading}
          error={errors.firstName?.message}
          {...register('firstName')}
        />
        <AuthInput
          id='lastName'
          label='Last Name'
          placeholder='Enter your last name'
          autoComplete='family-name'
          disabled={isLoading}
          error={errors.lastName?.message}
          {...register('lastName')}
        />
      </div>

      <AuthInput
        id='email'
        type='email'
        label='Email'
        placeholder='Enter your email'
        autoComplete='email'
        disabled={isLoading}
        error={errors.email?.message}
        {...register('email')}
      />

      <AuthInput
        id='password'
        type='password'
        label='Password'
        placeholder='Enter your password'
        autoComplete='new-password'
        disabled={isLoading}
        error={errors.password?.message}
        {...register('password')}
      />

      <AuthInput
        id='confirmPassword'
        type='password'
        label='Confirm Password'
        placeholder='Confirm your password'
        autoComplete='new-password'
        disabled={isLoading}
        error={errors.confirmPassword?.message}
        {...register('confirmPassword')}
      />

      <div className='ml-1 flex flex-col'>
        <div className='flex items-center space-x-2'>
          <input
            type='checkbox'
            id='terms'
            disabled={isLoading}
            {...register('agreed')}
            className='size-4 cursor-pointer rounded border-gray-300 accent-accent-glow fold:accent-brand-800'
          />
          <label
            htmlFor='terms'
            className='cursor-pointer font-sans text-14 font-medium leading-21 tracking-normal text-white fold:text-brand-800'
          >
            I agree to the{' '}
            <button
              type='button'
              onClick={(e) => {
                e.preventDefault();
                onTermsClick?.();
              }}
              className='text-white underline decoration-solid hover:text-gray-200 fold:text-accent-500 fold:hover:opacity-80'
            >
              Terms & Conditions
            </button>
          </label>
        </div>
        {errors.agreed && <p className='mt-1 text-xs text-red-400'>{errors.agreed.message}</p>}
      </div>

      {backendError && (
        <div className='rounded-md border border-red-500/20 bg-red-500/10 p-3'>
          <p className='text-center text-sm text-red-400'>{backendError}</p>
        </div>
      )}
    </form>
  );
};

RegisterForm.displayName = 'RegisterForm';
